import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Form, Row, Col } from "react-bootstrap";
import { X } from "react-bootstrap-icons";
import "./ExtractModel.css";
import PageLoading from "components/PageLoading";

import BonusRangeTable from "../ComponentsExtractAnalysis/RangeTable/BonusRangeTable";
import BonusDocumentTable from "../ComponentsExtractAnalysis/DocumentTable/BonusDocumentTable";
import GenericSmallTable from "../ComponentsExtractAnalysis/GenericTable/GenericSmallTable";
import DeducaoDocumentTable from "./ComponentsExtractModel/DeducaoTable/DeducaoTable";
import SupportTable from "../ComponentsExtractAnalysis/SupportTable/SupportTable";
import BonusDocumentTableAux from "./ComponentsExtractModel/BonusDocumentTableAux/BonusDocumentTableAux";

import { getSindicatoById } from "../../../../services";
import { getSalarioMinimoAtual } from "../../../../services/salarioMinimoService";
import {
  buildPreviewExtrato,
  getDeducoesExtrato,
} from "../../../../services/ExtractService";

interface ExtractModelProps {
  previewMode?: boolean;
  idResposta?: string;
  idSindicato?: string;
  previewData?: unknown;
  dadosSindicato?: unknown;
  deducaoData?: unknown;
  quantidadeAdimplentes?: number;
  receita?: number | unknown[];
  associado?: unknown[];
  percentual?: unknown;
  percentualReceita?: unknown;
  dadosValor?: unknown;
}

const ExtractModel = ({
  previewMode = false,
  idResposta: propIdResposta,
  idSindicato: propIdSindicato,
  previewData: propPreviewData,
  dadosSindicato: propDadosSindicato,
  deducaoData: propDeducaoData,
  quantidadeAdimplentes,
  receita,
  associado,
  percentual,
  dadosValor,
}: ExtractModelProps = {}) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateRef = useRef(location.state ?? null);
  const state = stateRef.current || {};
  const fetchDoneRef = useRef(false);

  const idResposta = propIdResposta || params.idResposta;
  const idSindicato = propIdSindicato || params.idSindicato;

  const [dadosSindicato, setDadosSindicato] = useState(
    propDadosSindicato || state.dadosSindicato || null,
  );
  const [previewData, setPreviewData] = useState(
    propPreviewData || state.previewData || null,
  );
  const [deducaoData, setDeducaoData] = useState(
    propDeducaoData || state.deducaoData || null,
  );

  const quantidadeAdimplentesFinal =
    quantidadeAdimplentes ?? state.quantidadeAdimplentes ?? 0;
  const receitaFinal = receita ?? state.receita ?? 0;
  const associadoFinal = associado ?? state.associado ?? [];

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [dozeSalarios, setDozeSalarios] = useState(0);

  const idsOk =
    !!idResposta &&
    !!idSindicato &&
    !isNaN(Number(idResposta)) &&
    !isNaN(Number(idSindicato));

  const receitaOperacional = Number(previewData?.receitaOperacional ?? 0);
  const receitaSuperiorDozeSalarios =
    dozeSalarios > 0 && receitaOperacional > dozeSalarios;

  useEffect(() => {
    const fetchSalarioMinimo = async () => {
      try {
        const { valor } = await getSalarioMinimoAtual();
        const salario = Number(valor);
        setDozeSalarios(Number.isFinite(salario) ? salario * 12 : 0);
      } catch (_err) {
        setDozeSalarios(0);
      }
    };

    if (idsOk) {
      fetchSalarioMinimo();
    }
  }, [idSindicato, idResposta, idsOk]);

  useEffect(() => {
    fetchDoneRef.current = false;
  }, [idSindicato, idResposta]);

  useEffect(() => {
    if (!idsOk) {
      setCarregando(false);
      return;
    }

    const temDadosDoPai =
      (propDadosSindicato && Object.keys(propDadosSindicato as object).length > 0) ||
      !!propPreviewData ||
      !!propDeducaoData;
    const temDadosDoState =
      !!state.dadosSindicato || !!state.previewData || !!state.deducaoData;

    if (temDadosDoPai || temDadosDoState) {
      setCarregando(false);
      return;
    }

    if (fetchDoneRef.current) {
      return;
    }
    fetchDoneRef.current = true;

    const fetchAll = async () => {
      try {
        const responseSindicato = await getSindicatoById(idSindicato);
        setDadosSindicato(responseSindicato.data || null);

        const payload = {
          idQuestionario: Number(idResposta),
          quantidadeAdimplentes: Number(quantidadeAdimplentesFinal) || 0,
          criteriosBonusQuestionario: Array.from({ length: 12 }, (_, i) => ({
            conforme: true,
            idCriterioBonus: i + 1,
          })),
        };
        const responsePreview = await buildPreviewExtrato(payload);
        setPreviewData(responsePreview);

        const responseDeducoes = await getDeducoesExtrato(idSindicato);
        setDeducaoData(responseDeducoes);
      } catch (_err) {
        setErro("Erro ao buscar dados do extrato.");
      } finally {
        setCarregando(false);
      }
    };

    fetchAll();
  }, [idSindicato, idResposta, idsOk]);

  const formatCNPJ = (cnpj) => {
    if (!cnpj || cnpj.length !== 14) return cnpj;
    return cnpj.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5",
    );
  };

  const handleClose = () => {
    if (!previewMode) {
      navigate(`/extract/${idResposta}/${idSindicato}`, { replace: true });
    }
  };

  const toBRL = (value) =>
    value != null
      ? Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })
      : "R$ 0,00";

  const calcularTotaisDeducoes = () => {
    const totalAtraso = deducaoData?.deducaoAtraso?.deduzir
      ? Number(deducaoData.deducaoAtraso.valorDeducao || 0)
      : 0;

    const totalTitulos = (deducaoData?.deducaoTitulos || [])
      .filter((item) => item.deduzir)
      .reduce((acc, item) => acc + Number(item.valor || 0), 0);

    const totalOutras = (deducaoData?.deducaoOutros || [])
      .filter((item) => item.deduzir)
      .reduce((acc, item) => acc + Number(item.valor || 0), 0);

    const totalGeral = totalAtraso + totalTitulos + totalOutras;
    const valorLiquido =
      Number(previewData?.valorTotalApoioBruto || 0) - totalGeral;

    return {
      totalAtraso,
      totalTitulos,
      totalOutras,
      totalGeral,
      valorLiquido,
    };
  };

  const { totalAtraso, totalTitulos, totalOutras, totalGeral, valorLiquido } =
    calcularTotaisDeducoes();

  if (carregando) return <PageLoading />;

  if (erro) return <p className="text-danger">{erro}</p>;

  const dadosResumo = [
    { label: "VALOR MÍNIMO", valor: toBRL(previewData?.valorMinimo) },
    { label: "BÔNUS ALCANÇADO", valor: toBRL(previewData?.valorBonus) },
    {
      label: "BÔNUS EXTRA ALCANÇADO",
      valor: toBRL(previewData?.valorBonusExtra),
    },
    {
      label: "VALOR TOTAL DO APOIO",
      valor: toBRL(previewData?.valorTotalApoioBruto),
    },
  ];

  return (
    <Container className="analysis-container py-4 position-relative">
      {!previewMode && (
        <button
          onClick={handleClose}
          className="close-button-fixed"
          aria-label="Fechar"
        >
          <X />
        </button>
      )}

      <Form className="mt-3">
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            value={dadosSindicato?.nome || ""}
            disabled
          />
        </Form.Group>

        <Form.Group>
          <Form.Label className="extract-label">CNPJ DO SINDICATO</Form.Label>
          <Form.Control
            type="text"
            value={formatCNPJ(dadosSindicato?.cnpj || "")}
            disabled
          />
        </Form.Group>

        <Row className="valor-receber">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="extract-label">
                RECEITA OPERACIONAL
              </Form.Label>
              <Form.Control
                type="text"
                value={toBRL(previewData?.receitaOperacional)}
                disabled
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <Row className="mt-3">
        <Col md={12}>
          <table className="mini-tabela-tabela">
            <thead>
              <tr>
                <th>RECEITA OPERACIONAL SUPERIOR A 12 SALÁRIOS</th>
                <th>QUANTIDADE DE ADIMPLENTES ENVIADOS</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  className={
                    receitaSuperiorDozeSalarios ? "green-text" : "red-text"
                  }
                >
                  {receitaSuperiorDozeSalarios ? "SIM" : "NÃO"}
                </td>
                <td className="green-text">{quantidadeAdimplentesFinal}</td>
              </tr>
            </tbody>
          </table>
        </Col>
      </Row>

      <BonusRangeTable
        tituloFaixa={previewData?.faixaReceitaBonus?.titulo}
        receitaMin={previewData?.faixaReceitaBonus?.receitaMin}
        receitaMax={previewData?.faixaReceitaBonus?.receitaMax}
        valorMinimo={previewData?.faixaReceitaBonus?.valorMinimo}
        valorBonusPotencial={
          previewData?.faixaReceitaBonus?.valorBonusPotencial
        }
        valorMaximoPotencial={
          previewData?.faixaReceitaBonus?.valorMaximoPotencial
        }
      />

      <div className="bonus-tables-wrapper">
        <BonusDocumentTableAux
          data={previewData?.criterioBonus || []}
          receita={receitaFinal}
          associado={associadoFinal}
          percentualReceita={percentual ?? 0}
          valor={previewData?.valorBonus}
        />
        <BonusDocumentTable
          data={previewData?.criterioBonusExtra || []}
          title={["DOCUMENTO BÔNUS EXTRA", "CUMPRIU?", "PERCENTUAL", "VALOR"]}
        />
      </div>

      <GenericSmallTable dados={dadosResumo || []} corValor="verde" />

      <br />

      <DeducaoDocumentTable
        atrasoDocs={
          deducaoData?.deducaoAtraso &&
            deducaoData.deducaoAtraso.deduzir !== false
            ? [deducaoData.deducaoAtraso]
            : []
        }
        titulosEmAberto={(deducaoData?.deducaoTitulos || []).filter(
          (t) => t.deduzir !== false,
        )}
        outrasDeducoes={(deducaoData?.deducaoOutros || []).filter(
          (o) => o.deduzir !== false,
        )}
        totalAtraso={toBRL(totalAtraso)}
        totalTitulos={toBRL(totalTitulos)}
        totalOutras={toBRL(totalOutras)}
        totalGeral={toBRL(totalGeral)}
        liquidoApoio={toBRL(valorLiquido)}
      />

      <SupportTable
        dadosValoresLiberados={[]}
        totalValoresLiberados="R$ 0,00"
        pendenteLiberacao="R$ 0,00"
        liquidoApoio={toBRL(valorLiquido)}
      />
    </Container>
  );
};

export default ExtractModel;
